import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import * as LucideIcons from "lucide-react";
import { Plus, Trash2, Pencil, ChevronDown, ChevronRight, Tag, X, Check } from "lucide-react";

function Icon({ name, className }) {
  const I = LucideIcons[name] || Tag;
  return <I className={className}/>;
}

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [subDraft, setSubDraft] = useState({}); // { [cat_id]: {name} }
  const [editingSub, setEditingSub] = useState(null); // {id, name}

  const load = () => api.get("/categories").then((r) => setCats(r.data || []));
  useEffect(() => { load(); }, []);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const addSub = async (catId) => {
    const name = (subDraft[catId]?.name || "").trim();
    if (!name) return toast.error("Nom requis");
    try {
      await api.post("/admin/subcategories", { category_id: catId, name, sort_order: 999 });
      setSubDraft((s) => ({ ...s, [catId]: { name: "" } }));
      toast.success("Sous-catégorie créée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const delSub = async (sub) => {
    if (!window.confirm(`Supprimer « ${sub.name} » ?`)) return;
    try {
      await api.delete(`/admin/subcategories/${sub.id}`);
      toast.success("Supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression bloquée");
    }
  };

  const saveEditSub = async () => {
    if (!editingSub) return;
    const name = (editingSub.name || "").trim();
    if (!name) return toast.error("Nom requis");
    try {
      await api.patch(`/admin/subcategories/${editingSub.id}`, { name });
      toast.success("Mise à jour");
      setEditingSub(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const delCat = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie « ${cat.name} » ?`)) return;
    try {
      await api.delete(`/admin/categories/${cat.id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression bloquée (catégorie utilisée)");
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <h1 className="font-heading text-3xl font-bold">Catégories & sous-catégories</h1>
      <p className="mt-1 text-sm text-slate-500">
        9 catégories principales avec leurs sous-catégories. Les sous-catégories utilisées par des commerces ou offres ne peuvent pas être supprimées.
      </p>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="admin-cat-tree">
        {cats.map((c) => {
          const isOpen = !!expanded[c.id];
          const subs = c.subcategories || [];
          return (
            <div key={c.id} data-testid={`admin-cat-row-${c.slug}`}>
              {/* Ligne catégorie */}
              <div className="flex items-center gap-3 p-4">
                <button onClick={() => toggle(c.id)}
                        className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-slate-100 grid place-items-center"
                        aria-label="Déplier"
                        data-testid={`admin-cat-toggle-${c.slug}`}>
                  {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                </button>
                <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center">
                  <Icon name={c.icon} className="w-5 h-5"/>
                </div>
                <div className="flex-1">
                  <div className="font-heading font-extrabold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">
                    /categories/<span className="font-mono">{c.slug}</span>
                    {" · "}{subs.length} sous-cat.
                    {" · "}{c.offer_count || 0} offres
                    {" · "}{c.business_count || 0} commerces
                  </div>
                </div>
                <button onClick={() => delCat(c)}
                        className="text-slate-400 hover:text-rose-600 p-2"
                        data-testid={`admin-cat-delete-${c.slug}`}
                        title="Supprimer (bloquée si utilisée)">
                  <Trash2 size={16}/>
                </button>
              </div>

              {/* Détail sous-catégories */}
              {isOpen && (
                <div className="pb-4 pl-14 pr-4" data-testid={`admin-cat-children-${c.slug}`}>
                  <div className="space-y-1.5">
                    {subs.map((s) => {
                      const editing = editingSub?.id === s.id;
                      return (
                        <div key={s.id} className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2"
                             data-testid={`admin-sub-row-${s.slug}`}>
                          <Tag size={14} className="text-slate-400"/>
                          {editing ? (
                            <>
                              <Input className="h-8 flex-1"
                                     value={editingSub.name}
                                     onChange={(e) => setEditingSub((v) => ({ ...v, name: e.target.value }))}
                                     onKeyDown={(e) => e.key === "Enter" && saveEditSub()}
                                     data-testid={`admin-sub-edit-input-${s.slug}`}/>
                              <button onClick={saveEditSub} className="p-1 text-emerald-700" data-testid={`admin-sub-edit-save-${s.slug}`}><Check size={16}/></button>
                              <button onClick={() => setEditingSub(null)} className="p-1 text-slate-500"><X size={16}/></button>
                            </>
                          ) : (
                            <>
                              <div className="flex-1 text-sm">
                                <span className="font-semibold text-slate-800">{s.name}</span>
                                <span className="ml-2 text-xs text-slate-500 font-mono">{s.slug}</span>
                              </div>
                              <div className="text-[11px] text-slate-500">
                                {s.offer_count || 0} offres · {s.business_count || 0} commerces
                              </div>
                              <button onClick={() => setEditingSub({ id: s.id, name: s.name })}
                                      className="p-1 text-slate-500 hover:text-slate-800"
                                      data-testid={`admin-sub-edit-btn-${s.slug}`}>
                                <Pencil size={14}/>
                              </button>
                              <button onClick={() => delSub(s)}
                                      className="p-1 text-slate-400 hover:text-rose-600"
                                      data-testid={`admin-sub-delete-${s.slug}`}
                                      title="Supprimer (bloquée si utilisée)">
                                <Trash2 size={14}/>
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Ajout */}
                  <div className="mt-3 flex gap-2 items-center">
                    <Input placeholder="Nouvelle sous-catégorie…"
                           value={subDraft[c.id]?.name || ""}
                           onChange={(e) => setSubDraft((s) => ({ ...s, [c.id]: { name: e.target.value } }))}
                           onKeyDown={(e) => e.key === "Enter" && addSub(c.id)}
                           className="max-w-sm h-9"
                           data-testid={`admin-sub-add-input-${c.slug}`}/>
                    <Button onClick={() => addSub(c.id)} className="h-9 btn-shine text-white"
                            data-testid={`admin-sub-add-btn-${c.slug}`}>
                      <Plus size={14} className="mr-1"/> Ajouter
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
